import { useEffect, useState, useRef, useMemo } from 'react';
import { useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import { useDispatch } from 'react-redux';
import {
    setComposeEmailModal,
    triggerRequirementsRefresh,
    setEngagementLetterData,
    setBureauKycConsentData,
    setOnePagerData,
} from '../../store/investor/action';
import { BASE_ROUTES } from '../../utils/constants/routesConst';
import { fetchCombinedOrgUsers, fetchCombinedDealUsers, getEmailTemplate, sendEmailNotification } from '../../store/investor/apiHelpers';
import { fetchEngagementLetterTemplate, fetchBureauKycConsentTemplate } from '../../store/investor/investorApiHelper';
import { getPresignedUrl } from '../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import type { Value } from 'platejs';
import { plateToHtml, exportEditorToPdfServer } from '../PlateEditor';
import type { MentionUser } from '../PlateEditor';
import { EMPTY_EDITOR_VALUE } from '../PlateEditor/types/types';
import { processImagesBeforeSave } from '../PlateEditor/services/imageUploadService';
import {
    COMPOSE_EMAIL_UPLOAD_SOURCE,
    SystemAttachmentKey,
    SYSTEM_ATTACHMENT_LABELS,
    SYSTEM_ATTACHMENT_ORDER,
    DOC_TYPE_SYSTEM_KEYS,
    COMPOSE_EMAIL_DRAWER_SOURCE,
    ATTACHMENT_TEMPLATE_ACCOUNT_TYPE,
    ATTACHMENT_PILL_NEW_TAB_TARGET,
} from './constants';
import { uploadPdfBlobAsAttachment } from './attachments/composeAttachmentUpload';
import type { SystemAttachmentToggles, SystemAttachmentConfigured, AttachmentPill, ManualUploadFile } from './types';
import { syncDataPendingTable, extractPendingRequirements, MAGIC_GST_LINK_TOKEN } from './dataPendingTable';
import { getBorrowerRequirements, getLenderRequirements } from '../NeedInfoV2/NeedInfoV2Helper';
import type { EntityRequirement } from '../NeedInfoV2/CustomHooks/useFetchRequirements';
import { fetchInternalSummonAicaData, InternalSummonUserType } from '../../store/user/actions';
import type { InternalSummonAicaDataPayload } from '../../store/user/actions';
import { toast } from 'react-toastify';
import { Mixpanel } from '../../utils/mixpanel';
import { AICA_COMPOSE_EMAIL_SENT } from '../../utils/constants/mixpanelEvents/investorEvents';
import { replaceTemplatePlaceholders, emailRegex, sumAttachmentBytes } from '../../utils/utils';
import { COMPOSE_EMAIL_SOURCE } from '../NeedInfoV2/NeedInfoV2Constants';
import { addAssessee } from '../../store/user/userV2/actions';
import { fetchCountryCodes } from '../../store/company/apiHelpers';
import { USER_TYPE, JOURNEY_NAME } from '../../utils/constants/user';
import { KEYBOARD_EVENTS } from '../../utils/constants/commonConstants';
import { MAX_ATTACHMENT_BYTES } from './ComposeEmailModal.constants';
import type { AttachmentMeta } from './ComposeEmailModal.types';

export const useComposeEmailLogic = ({
    isOpen,
    initialRecipients,
    initialNote,
}: {
    isOpen: boolean;
    initialRecipients?: Array<{ emailId: string; userId?: string; name?: string }>;
    /**
     * Optional pre-seed for the email note body.
     * Pass a Plate Value (array of nodes) for structured content (bold, links) — this
     * is what buildMagicSignNoteWithLinks returns and round-trips perfectly through
     * plateToHtml. Pass a plain string for legacy/simple callers — seeded as a single
     * unstyled paragraph.
     */
    initialNote?: string | Value;
}) => {
    const params = useParams();
    const user = useAppSelector((state) => state.user);
    const config = useAppSelector((state) => state.investor.config);
    const composeEmailData = useAppSelector((state) => state.investor.composeEmailData);
    const ownerDropdownOptions = useAppSelector((state) => state.investor.ownerDropdownOptions);
    // @-mentionable users for the PlateEditor body. Mapped to the MentionUser
    // shape and memoised on the slice so the editor keeps a stable array
    // identity across re-renders (avoids rebuilding the mention plugin).
    const ownerOptions = useMemo<MentionUser[]>(
        () => (ownerDropdownOptions ?? []).map((owner) => ({ userId: owner.userId, name: owner.name, emailId: owner.emailId })),
        [ownerDropdownOptions],
    );
    // Magic Sign callers seed initialRecipients; standard callers get the org-user list from API.
    const [selectedUsers, setSelectedUsers] = useState<any[]>(initialRecipients ?? []);
    const [selectableUsers, setSelectableUsers] = useState<any[]>([]);
    // AttachmentMeta (= ManualUploadFile + optional fileSizeBytes) so staging's
    // sumAttachmentBytes size-enforcement can read each file's byte size.
    const [filesMetaData, setFilesMetaData] = useState<AttachmentMeta[]>([]);
    const [attachSignature, setAttachSignature] = useState<boolean>(false);

    // System attachment toggles — all default OFF (RECUR-23138). Data Pending is
    // always available; Engagement Letter and Bureau & KYC Consent are gated by
    // whether their template is configured for the PNO (non-empty template fetch).
    const [systemToggles, setSystemToggles] = useState<SystemAttachmentToggles>({
        [SystemAttachmentKey.DATA_PENDING]: false,
        [SystemAttachmentKey.ENGAGEMENT_LETTER]: false,
        [SystemAttachmentKey.BUREAU_KYC_CONSENT]: false,
        [SystemAttachmentKey.ONE_PAGER]: false,
    });
    const [systemConfigured, setSystemConfigured] = useState<SystemAttachmentConfigured>({
        [SystemAttachmentKey.DATA_PENDING]: true,
        [SystemAttachmentKey.ENGAGEMENT_LETTER]: false,
        [SystemAttachmentKey.BUREAU_KYC_CONSENT]: false,
        // One Pager visibility is wired in a follow-up (deal-scoped config probe);
        // kept hidden until its drawer capture is in place.
        [SystemAttachmentKey.ONE_PAGER]: false,
    });
    // Generated + uploaded PDFs for document-backed system attachments
    // (EL / Bureau-KYC / One Pager). Keyed by SystemAttachmentKey; presence means
    // "attached" (the docId rides the email's docIds list). Populated on the
    // drawer's Save & Close via the view-to-attach handoff.
    const [systemAttachmentDocs, setSystemAttachmentDocs] = useState<Partial<Record<SystemAttachmentKey, ManualUploadFile>>>({});

    // Legacy alias kept so the send payload + disabled-state guards stay readable.
    const sendNeedInfo = systemToggles[SystemAttachmentKey.DATA_PENDING];
    const [searchParams, setSearchParams] = useSearchParams();
    // Seed the editor body from initialNote.
    //  - Value (array of nodes): used directly — Magic Sign callers pass a structured
    //    Plate Value from buildMagicSignNoteWithLinks so bold/links render correctly
    //    and plateToHtml serialises back to proper <strong>/<a> tags.
    //  - string: wrapped in a single unstyled paragraph — legacy/simple callers.
    //  - undefined: editor starts empty.
    const buildInitialUserInputValue = (): Value => {
        if (!initialNote) return EMPTY_EDITOR_VALUE;
        if (Array.isArray(initialNote)) return initialNote as Value;
        return [{ type: 'p', children: [{ text: initialNote }] }];
    };
    const [userInputText, setUserInputText] = useState<Value>(buildInitialUserInputValue);
    // Ref holds the latest editor value synchronously (no re-render cost).
    // setUserInputText is called on a trailing debounce so ActionButtons
    // re-renders at most once per pause, not once per keystroke. Seeded with
    // the same initial value as the state above so Magic Sign callers that
    // send/preview before the user has typed anything (reading the ref) see
    // the seeded note, not an empty one.
    // useRef has no lazy-init form (unlike useState), so the builder is guarded to run once
    // instead of being invoked (and its result thrown away) on every render.
    const userInputTextRef = useRef<Value>();
    if (userInputTextRef.current === undefined) {
        userInputTextRef.current = buildInitialUserInputValue();
    }
    const userInputTextDebounceRef = useRef<number | null>(null);
    // The live Plate editor instance, captured via PlateEditor's onEditorMount.
    // Held in state (not a ref) so the data-pending sync effect re-runs when a
    // fresh editor mounts on each modal open.
    const [composeEditor, setComposeEditor] = useState<any>(null);
    const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
    const [isSendLoading, setIsSendLoading] = useState<boolean>(false);
    const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);
    const [categoryId, setCategoryId] = useState<number>(0);
    const [defaultCountryData, setDefaultCountryData] = useState<any>(null);

    // User Email Input states
    const [inputValue, setInputValue] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredUsers, setFilteredUsers] = useState<string[]>([]);
    const [selectedPills, setSelectedPills] = useState<Set<string>>(new Set());
    const [isValidatingEmail, setIsValidatingEmail] = useState(false);

    // Refs for User Email Input
    const inputRef = useRef<HTMLInputElement>(null);
    const isSelectingFromDropdown = useRef<boolean>(false);
    const isRemovingUser = useRef<boolean>(false);

    const dispatch = useDispatch();
    const flowId = 16;
    const platformUserId = sessionStorage.getItem('platform_user_id') ?? localStorage.getItem('platform_user_id');
    const isLenderQuery = [COMPOSE_EMAIL_SOURCE.LENDER_QUERY, COMPOSE_EMAIL_SOURCE.DEALS_PAGE].includes(composeEmailData.source);

    // Pre-supplied requirement objects: only populated when opened from the
    // NeedInfoV2 Data Pending button (which carries selectedRequirementDetails).
    const dataPendingRequirements = useMemo<EntityRequirement[]>(
        () => composeEmailData.selectedRequirementDetails ?? [],
        [composeEmailData.selectedRequirementDetails],
    );
    // Pending requirements fetched on open for the normal flows (dashboard header /
    // leads / deals) — those entry points don't carry requirement details, so the
    // Data Pending toggle would otherwise have nothing to render.
    const [fetchedDataPendingRequirements, setFetchedDataPendingRequirements] = useState<EntityRequirement[]>([]);
    // The requirements the in-body table actually renders: the carried selection
    // when opened from the Data Pending button, else the fetched pending list.
    const effectiveDataPendingRequirements = useMemo<EntityRequirement[]>(
        () => (dataPendingRequirements.length ? dataPendingRequirements : fetchedDataPendingRequirements),
        [dataPendingRequirements, fetchedDataPendingRequirements],
    );
    // Resolved GST magic-link URL (org-scoped, fetched once per open) used to swap
    // the MAGIC_LINK_GST_R1 token in GST-connect rows for a real clickable link.
    const [resolvedGstLink, setResolvedGstLink] = useState<string | null>(null);
    // The Data Pending button (borrower → DATA_PENDING, lender → LENDER_QUERY) is
    // the only entry point that carries requirements; default the toggle ON there.
    const isFromDataPendingButton = [COMPOSE_EMAIL_SOURCE.DATA_PENDING, COMPOSE_EMAIL_SOURCE.LENDER_QUERY].includes(
        composeEmailData.source,
    );
    const shouldDefaultDataPendingOn = isFromDataPendingButton && dataPendingRequirements.length > 0;

    const handleEditorMount = useCallback((editor: any) => setComposeEditor(editor), []);

    /**
     * Get the organization ID based on the source of the compose email modal
     * @returns string
     */
    const getOrgIdBasedOnSource = () => {
        switch (composeEmailData.source) {
            case COMPOSE_EMAIL_SOURCE.LENDER_QUERY:
                return composeEmailData.lenderId || '';
            case COMPOSE_EMAIL_SOURCE.DATA_PENDING:
            case COMPOSE_EMAIL_SOURCE.DASHBOARD_HEADER:
                return params.id || (searchParams.get('orgId') as string) || '';
            case COMPOSE_EMAIL_SOURCE.LEADS:
            case COMPOSE_EMAIL_SOURCE.DEALS_PAGE:
                return composeEmailData.orgId || '';
            default:
                return '';
        }
    };
    const orgId = getOrgIdBasedOnSource();

    // GST-connect requirements carry a magic-link token the backend used to
    // resolve server-side. Resolve it client-side (org-scoped) so the in-body
    // table can render a real, clickable GST link the way the old emails did.
    const generateGstMagicLink = useCallback((): Promise<string> => {
        return new Promise((resolve, reject) => {
            const payload: InternalSummonAicaDataPayload = {
                parentOrgId: user?.userData?.investorOrgId || '',
                parentEmailId: user?.userData?.investorPrimaryEmail || '',
                orgId,
                userType: InternalSummonUserType.ASSESSEE,
                requesterUserId: platformUserId || '',
                isMagicLink: true,
            };
            dispatch(
                fetchInternalSummonAicaData(payload, {
                    onSuccess: (_userData: any, link: string) => resolve(`https://${link}`),
                    onError: () => reject(new Error('Failed to generate GST magic link')),
                }),
            );
        });
    }, [dispatch, orgId, user?.userData?.investorOrgId, user?.userData?.investorPrimaryEmail, platformUserId]);

    // Resolve the GST magic link once per open, only when a requirement needs it.
    useEffect(() => {
        if (!isOpen || resolvedGstLink || !orgId) return;
        if (!effectiveDataPendingRequirements.some((req) => req.link === MAGIC_GST_LINK_TOKEN)) return;
        let cancelled = false;
        generateGstMagicLink()
            .then((url) => {
                if (!cancelled) setResolvedGstLink(url);
            })
            .catch((err) => {
                // Leave unresolved — the row keeps its text, just without a link.
                // Logged so a missing GST link reads as "resolution failed" rather
                // than silently looking like the feature isn't wired.
                console.error('[Compose] GST magic-link resolution failed', err);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, orgId, effectiveDataPendingRequirements, resolvedGstLink, generateGstMagicLink]);

    // Requirements fed to the in-body table, with the GST magic-link token swapped
    // for the resolved URL once it is available.
    const tableRequirements = useMemo<EntityRequirement[]>(
        () =>
            resolvedGstLink
                ? effectiveDataPendingRequirements.map((req) =>
                      req.link === MAGIC_GST_LINK_TOKEN ? { ...req, link: resolvedGstLink } : req,
                  )
                : effectiveDataPendingRequirements,
        [effectiveDataPendingRequirements, resolvedGstLink],
    );

    /**
     * Combined function to fetch users based on the type (org users or deal users)
     * @param type - 'org' for organization users, 'deal' for deal share users
     * @param preserveSelectedUsers - If true, preserves existing selectedUsers instead of overwriting with API response
     * @returns Promise<void>
     */
    const fetchUsersByType = useCallback(
        async (type: 'org' | 'deal', preserveSelectedUsers = false) => {
            try {
                let usersData;
                let currentUsers;

                if (type === 'org') {
                    // Fetch combined org users
                    usersData = await fetchCombinedOrgUsers({
                        companyOrgId: orgId ?? ('' as string),
                        partnerOrgId: searchParams.get('partnerOrgId') ?? composeEmailData.partnerOrgId ?? ('' as string),
                    });

                    // Get current users (for To section pills) - existing implementation
                    currentUsers = usersData
                        ? [...(usersData?.currentUsers?.assesseeUsers || []), ...(usersData?.currentUsers?.partnerUsers || [])]
                        : [];
                } else {
                    // Fetch deal share users
                    const dealId = searchParams.get('dealId') || composeEmailData.dealId;
                    if (!dealId) {
                        console.warn('No dealId found for fetching deal share users');
                        return;
                    }

                    usersData = await fetchCombinedDealUsers({
                        companyOrgId: orgId,
                        dealId,
                    });

                    // Get current users (for To section pills)
                    currentUsers = usersData?.currentUsers || [];
                }

                // Callers that pass initialRecipients (e.g. Magic Sign) own the To-list seed —
                // the org-users fetch must not overwrite it
                const keepSelected = preserveSelectedUsers || !!initialRecipients?.length;
                if (usersData) {
                    // Use allUsers directly for dropdown
                    const allAvailableUsers = usersData?.allUsers || [];

                    // Set selectable users to all available users
                    setSelectableUsers(allAvailableUsers);

                    // Only update selectedUsers if we're not preserving them (initial load or explicit refresh)
                    if (!keepSelected) {
                        // Initially select only current users (not additional users from allUsers)
                        setSelectedUsers(currentUsers);
                    }
                } else {
                    setSelectableUsers([]);
                    if (!keepSelected) {
                        setSelectedUsers([]);
                    }
                }
            } catch (error) {
                const errorType = type === 'org' ? 'organization' : 'deal share';
                console.error(`Error fetching combined ${errorType} users:`, error);
            }
        },
        [orgId, searchParams, composeEmailData.partnerOrgId, composeEmailData.dealId, initialRecipients],
    );

    /**
     * Fetches the combined org user list of company and partner users
     * @returns Promise<void>
     */
    const getCombinedOrgUserList = useCallback(async () => {
        await fetchUsersByType('org');
    }, [fetchUsersByType]);

    /**
     * Fetches the deal share users for lender queries
     * @returns Promise<void>
     */
    const getDealShareUserList = useCallback(async () => {
        await fetchUsersByType('deal');
    }, [fetchUsersByType]);

    /**
     * Refreshes only selectableUsers (for dropdown) without affecting selectedUsers
     * This is used after adding a new user to update the dropdown list without restoring removed users
     * @returns Promise<void>
     */
    const refreshSelectableUsersOnly = useCallback(async () => {
        await fetchUsersByType(isLenderQuery ? 'deal' : 'org', true);
    }, [fetchUsersByType, isLenderQuery]);

    /**
     * Fetches organization users for the current organization
     * In case of data pending, it fetches the combined org user list of company and partner users
     * For lender queries, it fetches deal share users
     * @returns Promise<void>
     */
    const fetchUsers = useCallback(async (): Promise<void> => {
        try {
            setIsModalLoading(true);
            if (isLenderQuery) {
                await getDealShareUserList();
            } else {
                await getCombinedOrgUserList();
            }
        } catch (error) {
            console.error('Error fetching organization users:', error);
        } finally {
            setIsModalLoading(false);
        }
    }, [isLenderQuery, getDealShareUserList, getCombinedOrgUserList]);

    useEffect(() => {
        if (isOpen) {
            fetchUsers();
            setCategoryId(config?.dataConfig?.staging?.categoryId || 0);
        } else {
            // Reset the seed guard so the next open re-seeds from the new row's recipients.
            seededRowKeyRef.current = '';
        }
    }, [isOpen, fetchUsers, config?.dataConfig?.staging?.categoryId]);

    // Re-seed selectedUsers once per modal-open/row change, guarded by a stable rowId ref.
    // Using a ref avoids overwriting in-progress edits when the parent re-renders
    // (which would rebuild the initialRecipients array reference on every render).
    const seededRowKeyRef = useRef<string>('');
    useEffect(() => {
        if (!isOpen || !initialRecipients || initialRecipients.length === 0) return;
        // Build a stable key from the recipient ids so we only re-seed on a genuine row change.
        const rowKey = initialRecipients.map((r) => r.userId ?? r.emailId).join('|');
        if (seededRowKeyRef.current === rowKey) return;
        seededRowKeyRef.current = rowKey;
        setSelectedUsers(initialRecipients);
    }, [isOpen, initialRecipients]);

    // On modal open, probe which optional attachments are configured for this
    // PNO. "Configured" = the template fetch returns a non-empty array. Rows for
    // unconfigured attachments are hidden entirely. Toggles always start OFF
    // (RECUR-23138) — the previous auto-ON for DATA_PENDING / LENDER_QUERY is gone.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;

        const probeConfiguredTemplates = async () => {
            const investorOrgId = user?.userData?.investorOrgId || '';
            const primaryEmail = user?.userData?.primaryEmail || '';

            const [engagementLetterTemplate, bureauKycConsentTemplate] = await Promise.all([
                fetchEngagementLetterTemplate({
                    accountType: ATTACHMENT_TEMPLATE_ACCOUNT_TYPE,
                    pnoId: investorOrgId,
                    orgId,
                    primaryEmail,
                }),
                fetchBureauKycConsentTemplate({
                    accountType: ATTACHMENT_TEMPLATE_ACCOUNT_TYPE,
                    assesseeOrgId: orgId,
                    investorOrgId,
                    primaryEmail,
                }),
            ]);

            if (cancelled) return;
            setSystemConfigured({
                [SystemAttachmentKey.DATA_PENDING]: true,
                [SystemAttachmentKey.ENGAGEMENT_LETTER]: Array.isArray(engagementLetterTemplate) && engagementLetterTemplate.length > 0,
                // Bureau-KYC view-to-attach is wired (BureauKycConsentV2 → shared
                // PlateDocumentDrawer runs onComposeAttach on Save & Close), so show
                // it whenever a template is configured — same probe as EL above.
                [SystemAttachmentKey.BUREAU_KYC_CONSENT]: Array.isArray(bureauKycConsentTemplate) && bureauKycConsentTemplate.length > 0,
                // One Pager (deal-scoped) view-to-attach not wired yet — kept hidden
                // until DealSummaryEditorPlate runs onComposeAttach.
                [SystemAttachmentKey.ONE_PAGER]: false,
            });
        };

        probeConfiguredTemplates();
        return () => {
            cancelled = true;
        };
    }, [isOpen, orgId, user?.userData?.investorOrgId, user?.userData?.primaryEmail]);

    // Reset toggles whenever the modal opens. Data Pending defaults ON when the
    // modal was opened from the Data Pending button (it carries the requirements
    // to render); every other toggle defaults OFF (RECUR-23138).
    useEffect(() => {
        if (isOpen) {
            setSystemToggles({
                [SystemAttachmentKey.DATA_PENDING]: shouldDefaultDataPendingOn,
                [SystemAttachmentKey.ENGAGEMENT_LETTER]: false,
                [SystemAttachmentKey.BUREAU_KYC_CONSENT]: false,
                [SystemAttachmentKey.ONE_PAGER]: false,
            });
            setSystemAttachmentDocs({});
            setAttachSignature(false);
        }
    }, [isOpen, shouldDefaultDataPendingOn]);

    // Normal-flow opens (dashboard header / leads / deals) don't carry requirement
    // details the way the Data Pending button does, so fetch the org/deal's pending
    // requirements on open — otherwise toggling Data Pending has nothing to render.
    // Skipped when details were already carried in (the Data Pending button flow).
    useEffect(() => {
        if (!isOpen || dataPendingRequirements.length) return;
        let cancelled = false;
        const loadPendingRequirements = async () => {
            const dealId = searchParams.get('dealId') || composeEmailData.dealId || '';
            const res = isLenderQuery
                ? await getLenderRequirements({ dealId, isArchived: false })
                : await getBorrowerRequirements({ orgId, pnoId: user?.userData?.investorOrgId || '', isArchived: false });
            if (cancelled) return;
            setFetchedDataPendingRequirements(extractPendingRequirements(res));
        };
        loadPendingRequirements();
        return () => {
            cancelled = true;
        };
    }, [
        isOpen,
        dataPendingRequirements.length,
        isLenderQuery,
        orgId,
        user?.userData?.investorOrgId,
        composeEmailData.dealId,
        searchParams,
    ]);

    // Keep the Data Pending table in the editor body in lockstep with its toggle:
    // inserted when ON (and requirements exist), stripped when OFF. Re-runs when a
    // fresh editor mounts, the toggle flips, or the requirement set changes.
    useEffect(() => {
        if (!composeEditor) return;
        syncDataPendingTable(composeEditor, systemToggles[SystemAttachmentKey.DATA_PENDING], tableRequirements);
    }, [composeEditor, systemToggles, tableRequirements]);

    // Clear fetched requirements + resolved link when the modal closes so the next
    // open starts fresh (a different org needs its own GST link).
    useEffect(() => {
        if (!isOpen) {
            setFetchedDataPendingRequirements([]);
            setResolvedGstLink(null);
        }
    }, [isOpen]);

    // Cleanup sessionStorage and pending debounce on component unmount
    useEffect(() => {
        return () => {
            sessionStorage.removeItem('emailPreviewData');
            if (userInputTextDebounceRef.current) window.clearTimeout(userInputTextDebounceRef.current);
        };
    }, []);

    useEffect(() => {
        const emailTo = [COMPOSE_EMAIL_SOURCE.LENDER_QUERY, COMPOSE_EMAIL_SOURCE.DEALS_PAGE].includes(composeEmailData.source)
            ? 'Lender'
            : 'Company';
        const trackingData = {
            PNO_ID: user?.userData?.investorOrgId || '',
            USER_ID: user?.userData?.userId || '',
            OPENED_FROM: composeEmailData.source || '',
            EMAIL_TO: emailTo,
            ...(emailTo === 'Company' ? { COMPANY_ID: composeEmailData.orgId } : { LENDER_ID: composeEmailData.orgId }),
        };

        Mixpanel.track('Compose Email Opened', trackingData);
    }, []);

    const handleFileUpload = useCallback((fileMetaData: { docName: string; id: number; fileSizeBytes?: number }) => {
        setFilesMetaData((prevFiles) => {
            const currentFiles = Array.isArray(prevFiles) ? prevFiles : [];
            return [...currentFiles, fileMetaData];
        });
    }, []);

    // Upload context for materialising a generated doc PDF into Mnemosyne —
    // mirrors what AttachmentDropdown threads to UploadCard for manual uploads.
    const uploadCfg = useMemo(
        () => ({
            assesseeOrgId: orgId,
            mnemosyneOrgId: user?.userData?.investorOrgId || '',
            categoryId,
            flowId,
        }),
        [orgId, user?.userData?.investorOrgId, categoryId],
    );

    // View-to-attach handoff (RECUR-23138): the doc's drawer calls this on
    // Save & Close, handing back its rendered editor canvas. We export it to a
    // vector PDF (server Lambda, raster fallback), upload it to get a docId, and
    // record it as this system attachment — which flips the toggle ON and rides
    // the email's docIds list (no backend attach-flag support needed).
    const handleSystemDocAttached = useCallback(
        async (key: SystemAttachmentKey, canvasEl: HTMLElement, filename: string) => {
            try {
                const blob = await exportEditorToPdfServer({ target: canvasEl, filename, returnBlob: true });
                if (!(blob instanceof Blob)) {
                    toast.error('Could not attach the document. Please try again.');
                    return;
                }
                const meta = await uploadPdfBlobAsAttachment(blob, filename, uploadCfg);
                setSystemAttachmentDocs((prev) => ({ ...prev, [key]: meta }));
                setSystemToggles((prev) => ({ ...prev, [key]: true }));
            } catch (error) {
                console.error('Error attaching system document:', error);
                toast.error('Failed to attach the document. Please try again.');
            }
        },
        [uploadCfg],
    );

    // Open the editor drawer for a document-backed system attachment, gated to
    // the COMPOSE_EMAIL source so the drawer runs the post-save capture above.
    const openSystemDocDrawer = useCallback(
        (key: SystemAttachmentKey) => {
            const companyName = composeEmailData.orgName || '';
            const onComposeAttach = (canvasEl: HTMLElement, filename: string) => handleSystemDocAttached(key, canvasEl, filename);
            switch (key) {
                case SystemAttachmentKey.ENGAGEMENT_LETTER:
                    dispatch(
                        setEngagementLetterData({ open: true, source: COMPOSE_EMAIL_DRAWER_SOURCE, orgId, companyName, onComposeAttach }),
                    );
                    break;
                case SystemAttachmentKey.BUREAU_KYC_CONSENT:
                    dispatch(
                        setBureauKycConsentData({ open: true, source: COMPOSE_EMAIL_DRAWER_SOURCE, orgId, companyName, onComposeAttach }),
                    );
                    break;
                case SystemAttachmentKey.ONE_PAGER:
                    dispatch(
                        setOnePagerData({
                            open: true,
                            source: COMPOSE_EMAIL_DRAWER_SOURCE,
                            assesseeOrgId: orgId,
                            companyName,
                            categoryId,
                            flowId,
                            onComposeAttach,
                        }),
                    );
                    break;
            }
        },
        [dispatch, orgId, categoryId, composeEmailData.orgName, handleSystemDocAttached],
    );

    // Toggle a single system attachment on/off.
    //   • Document-backed (EL / Bureau-KYC / One Pager): turning ON opens the
    //     doc's drawer — the toggle only flips ON after Save & Close attaches
    //     it; turning OFF drops the generated doc + its pill.
    //   • Data Pending (flag-only): plain boolean flip.
    const handleToggleSystemAttachment = useCallback(
        (key: SystemAttachmentKey, value: boolean) => {
            if (DOC_TYPE_SYSTEM_KEYS.includes(key)) {
                if (value) {
                    openSystemDocDrawer(key);
                } else {
                    setSystemAttachmentDocs((prev) => {
                        const next = { ...prev };
                        delete next[key];
                        return next;
                    });
                    setSystemToggles((prev) => ({ ...prev, [key]: false }));
                }
                return;
            }
            setSystemToggles((prev) => ({ ...prev, [key]: value }));
        },
        [openSystemDocDrawer],
    );

    // Remove a manually-uploaded file pill by its docId.
    const handleRemoveManualFile = useCallback((docId: number) => {
        setFilesMetaData((prevFiles) => prevFiles.filter((file) => file?.id !== docId));
    }, []);

    // Staging RCPI-10190: total attachment size + 9MB over-limit guard used to
    // disable Send (manual uploads carry fileSizeBytes via AttachmentMeta).
    const totalAttachmentBytes = useMemo(() => sumAttachmentBytes(filesMetaData), [filesMetaData]);
    const isOverAttachmentLimit = useMemo(() => totalAttachmentBytes > MAX_ATTACHMENT_BYTES, [totalAttachmentBytes]);

    const handleToggleAttachSignature = useCallback((value: boolean) => {
        setAttachSignature(value);
    }, []);

    const getSignatureData = useCallback(() => {
        const userFullName = user?.userData?.userName || '';
        const userJobRole = user?.userData?.userJobRole || '';
        const pnoName = user?.userData?.pnoName || '';

        return {
            userFullName,
            userJobRole,
            pnoName,
        };
    }, [user?.userData?.userName, user?.userData?.pnoName, user?.userData?.userJobRole]);

    const handleUploadStatusChange = useCallback((isUploading: boolean) => {
        setIsUploadingFiles(isUploading);
    }, []);

    /**
     * Helper function to normalize email (remove alias part)
     * @param email - Email to normalize
     * @returns Normalized email
     */
    const normalizeEmail = (email: string): string => {
        const [localPart, domain] = email.split('@');
        if (!localPart || !domain) return email;
        // Remove the + alias part (e.g., user+123@domain.com -> user@domain.com)
        const baseLocalPart = localPart.split('+')[0];
        return `${baseLocalPart}@${domain}`;
    };

    /**
     * Checks if an email already exists in any user list (with deduplication)
     * @param email - Email to check
     * @returns Object with exists flag and existing user info
     */
    const checkEmailExists = useCallback(
        (email: string): { exists: boolean; isInSelectable: boolean; existingUser?: any } => {
            const normalizedInputEmail = normalizeEmail(email.toLowerCase());

            // Check if already in selected users
            const inSelected = selectedUsers.some((user) => normalizeEmail(user.emailId.toLowerCase()) === normalizedInputEmail);
            if (inSelected) {
                return { exists: true, isInSelectable: false };
            }

            // Check if in selectable users (existing user in platform)
            const selectableUser = selectableUsers.find(
                (user) => normalizeEmail(user?.emailId?.toLowerCase() || '') === normalizedInputEmail,
            );
            if (selectableUser) {
                return { exists: true, isInSelectable: true, existingUser: selectableUser };
            }

            return { exists: false, isInSelectable: false };
        },
        [selectableUsers, selectedUsers],
    );

    /**
     * Creates a new user in the system using AddAssessee API
     * @param email - Email of the user to create
     * @returns Promise<any> - Returns the created user data from API response
     */
    const createExternalUser = useCallback(
        async (email: string): Promise<any> => {
            return new Promise((resolve, reject) => {
                // If no country data is loaded yet, fetch it first
                if (!defaultCountryData) {
                    fetchCountryCodes({
                        onSuccess: (countryList: any[]) => {
                            if (countryList && countryList.length > 0) {
                                const countryData = countryList[0];
                                setDefaultCountryData(countryData);
                                createUserWithCountryData(email, countryData, resolve, reject);
                            } else {
                                reject(new Error('Failed to fetch country codes'));
                            }
                        },
                        onError: (error: any) => {
                            const errorMessage = error?.responseData?.responseMessage || 'Failed to fetch country codes';
                            toast.error(errorMessage);
                            reject(new Error(errorMessage));
                        },
                    });
                } else {
                    createUserWithCountryData(email, defaultCountryData, resolve, reject);
                }
            });
        },
        [dispatch, user, orgId, defaultCountryData],
    );

    /**
     * Helper function to create user with country data
     * Uses /v2/access/createUser API endpoint
     */
    const createUserWithCountryData = (email: string, countryData: any, resolve: Function, reject: Function) => {
        const domainMetadata = user?.domainMetadata;
        const countryCode = countryData?.countryCode || 'IN';
        const countryId = countryData?.countryId || 'IN';

        // Determine account type based on whether it's a lender or borrower context
        const accountType = isLenderQuery ? USER_TYPE.LENDER : USER_TYPE.COMPANY;
        const journeyName = isLenderQuery ? JOURNEY_NAME.INVITE_LENDER : JOURNEY_NAME.ADD_BORROWER_USER;

        dispatch(
            addAssessee({
                payload: {
                    user: {
                        emailId: email,
                        mobileNumber: '', // Empty phone number as per requirement
                        name: email.split('@')[0],
                        customFields: {
                            jobRole: '', // Empty job role as per requirement
                        },
                        countryCode: countryCode,
                        accountType: accountType,
                        accountTypes: [accountType],
                        journeyName: journeyName,
                        countryId: countryId,
                        investorId: user.userData.investorOrgId,
                        organizationId: orgId,
                        triggerNotification: false, // Don't send notification for external users added from email threads
                    },
                    platformId: domainMetadata?.productName ?? '',
                },
                handlers: {
                    onSuccess: (response: any) => {
                        toast.success(`User ${email} added successfully`);
                        resolve(response?.user);
                    },
                    onError: (error: any) => {
                        const errorMessage = error?.responseData?.responseMessage || 'Failed to create external user';
                        const responseCode = error?.responseData?.responseCode;
                        console.error('Error creating external user:', errorMessage, 'Response Code:', responseCode);

                        // Toast is already shown by the saga, no need to show again here
                        // Just reject with error details for caller
                        reject({ message: errorMessage, responseCode, alreadyShownToast: true });
                    },
                },
            }),
        );
    };

    /**
     * Handles adding a user by email
     * @param email - Email to add
     * @param isExternal - Whether the user is external (not in selectable users)
     */
    const handleAddUser = useCallback(
        async (email: string, isExternal = false) => {
            // Check if email already exists with deduplication
            const { exists, isInSelectable, existingUser } = checkEmailExists(email);

            if (exists && !isInSelectable) {
                // Already selected
                return;
            }

            if (exists && isInSelectable && existingUser) {
                // Existing user in platform, add to selected
                setSelectedUsers((prev) => [...prev, { ...existingUser, isExternalUser: false }]);
                return;
            }

            if (isExternal) {
                // External user - create in system first
                try {
                    const createdUserData = await createExternalUser(email);

                    // After successful creation, add to selected users with external flag and userId from response
                    setSelectedUsers((prev) => [
                        ...prev,
                        {
                            emailId: createdUserData?.emailId || email,
                            userId: createdUserData?.platformUserId,
                            isExternalUser: true,
                        },
                    ]);
                    // Refresh only selectableUsers (for dropdown) without affecting selectedUsers
                    // This ensures removed users don't come back when adding a new user
                    await refreshSelectableUsersOnly();
                } catch (error: any) {
                    // Error toast already shown in createExternalUser/createUserWithCountryData
                    // Just re-throw to let caller handle it
                    throw error;
                }
            } else {
                // Check if email is in selectable users
                const userToAdd = selectableUsers.find((user) => user?.emailId === email);
                if (userToAdd) {
                    setSelectedUsers((prev) => [...prev, { ...userToAdd, isExternalUser: false }]);
                }
            }
        },
        [selectableUsers, selectedUsers, checkEmailExists, createExternalUser, refreshSelectableUsersOnly],
    );

    /**
     * Handles removing a user by email
     * @param email - Email to remove
     */
    const handleRemoveUser = useCallback(
        (email: string) => {
            // Prevent removing the last user - ensure at least 1 user remains
            if (selectedUsers.length <= 1) {
                console.warn('Cannot remove the last user. At least one recipient must be selected.');
                return;
            }
            // Set flag to prevent blur handler from clearing input
            isRemovingUser.current = true;
            setSelectedUsers((prev) => prev.filter((user) => user.emailId !== email));
            // Reset flag after blur handler has had time to process (blur handler has 150ms delay)
            setTimeout(() => {
                isRemovingUser.current = false;
            }, 200);
        },
        [selectedUsers.length],
    );

    // Filter users based on input value
    useEffect(() => {
        if (inputValue.trim()) {
            const selectedEmails = selectedUsers.map((user) => user.emailId.toLowerCase());
            const selectableEmails = selectableUsers.map((user) => user?.emailId).filter(Boolean);
            const filtered = selectableEmails.filter(
                (email) => email.toLowerCase().includes(inputValue.toLowerCase()) && !selectedEmails.includes(email.toLowerCase()),
            );
            setFilteredUsers(filtered);
            setShowDropdown(filtered.length > 0);
        } else {
            setFilteredUsers([]);
            setShowDropdown(false);
        }
    }, [inputValue, selectableUsers, selectedUsers]);

    /**
     * Handle input change
     * @param e - React change event
     */
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    }, []);

    /**
     * Handle input key press (Enter, Backspace, Escape)
     * @param e - React keyboard event
     */
    const handleKeyPress = useCallback(
        async (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === KEYBOARD_EVENTS.ENTER) {
                e.preventDefault();
                const email = inputValue.trim();

                if (!email) return;

                // Step 1: Validate email format
                const isValidFormat = emailRegex.test(email);

                if (!isValidFormat) {
                    toast.error('Please enter a valid email address');
                    setTimeout(() => {
                        setInputValue('');
                    }, 500);
                    return;
                }

                // Step 2: Check if email is already selected (with normalization for alias handling)
                const normalizedInputEmail = normalizeEmail(email.toLowerCase());
                const isAlreadySelected = selectedUsers.some((user) => normalizeEmail(user.emailId.toLowerCase()) === normalizedInputEmail);

                if (isAlreadySelected) {
                    toast.warning('This email is already added to recipients');
                    setInputValue('');
                    return;
                }

                // Step 3: Check if email is in selectable users (existing user in platform)
                const allowedEmails = selectableUsers.map((user) => user?.emailId).filter(Boolean);
                const allowedEmailsLower = allowedEmails.map((email) => email.toLowerCase());
                const isExistingUser = allowedEmailsLower.includes(email.toLowerCase());

                if (isExistingUser) {
                    // Existing user - just add to selection
                    const originalEmail = allowedEmails.find((allowedEmail) => allowedEmail.toLowerCase() === email.toLowerCase()) || email;
                    await handleAddUser(originalEmail, false);
                } else {
                    // Step 4: New external user - create via /v2/access/createUser API
                    setIsValidatingEmail(true);
                    try {
                        await handleAddUser(email, true);
                    } catch (error) {
                        // Error already handled in parent component with toast
                        setIsValidatingEmail(false);
                        return;
                    } finally {
                        setIsValidatingEmail(false);
                    }
                }

                setInputValue('');
                setShowDropdown(false);
                setSelectedPills(new Set());
            } else if (e.key === KEYBOARD_EVENTS.BACKSPACE) {
                // Handle backspace for pill selection and deletion
                // Early return if input has value or no users selected
                if (inputValue !== '' || selectedUsers.length === 0) {
                    return;
                }

                e.preventDefault();

                // No pills selected - select the last pill
                if (selectedPills.size === 0) {
                    const lastUser = selectedUsers[selectedUsers.length - 1];
                    setSelectedPills(new Set([lastUser.emailId]));
                    return;
                }

                // Remove all selected pills (but ensure at least one user remains)
                const remainingUsersCount = selectedUsers.length - selectedPills.size;
                if (remainingUsersCount < 1) {
                    // Cannot remove all users - at least one must remain
                    toast.warning('At least one recipient must remain');
                    setSelectedPills(new Set());
                    return;
                }

                selectedPills.forEach((email) => {
                    handleRemoveUser(email);
                });
                setSelectedPills(new Set());
            } else if (e.key === 'Escape') {
                setShowDropdown(false);
                setInputValue('');
                setSelectedPills(new Set());
            } else {
                // Clear pill selection when typing
                if (selectedPills.size > 0) {
                    setSelectedPills(new Set());
                }
            }
        },
        [inputValue, selectedUsers, selectableUsers, selectedPills, handleAddUser, handleRemoveUser, normalizeEmail],
    );

    /**
     * Handle dropdown item click
     * @param email - Email to add
     */
    const handleDropdownItemClick = useCallback(
        async (email: string) => {
            // Set flag to prevent blur handler from processing
            isSelectingFromDropdown.current = true;

            try {
                await handleAddUser(email, false);
                setInputValue('');
                setShowDropdown(false);
                setSelectedPills(new Set());
                inputRef.current?.focus();
            } catch (error) {
                // Error already handled in parent component with toast
            } finally {
                // Always reset flag after a delay to ensure blur handler has completed
                setTimeout(() => {
                    isSelectingFromDropdown.current = false;
                }, 200);
            }
        },
        [handleAddUser],
    );

    /**
     * Handle input blur
     * @param e - React focus event
     */
    const handleInputBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        // Delay hiding dropdown to allow for dropdown clicks
        setTimeout(() => {
            // Skip processing if we're selecting from dropdown or removing a user
            if (isSelectingFromDropdown.current || isRemovingUser.current) {
                // Reset the flag after processing
                isRemovingUser.current = false;
                return;
            }

            // Only close dropdown and clear input on blur
            // User creation should only happen on Enter key press
            setInputValue('');
            setShowDropdown(false);
            setSelectedPills(new Set());
        }, 150);
    }, []);

    /**
     * Handle input focus
     */
    const handleInputFocus = useCallback(() => {
        if (inputValue.trim() && filteredUsers.length > 0) {
            setShowDropdown(true);
        }
    }, [inputValue, filteredUsers]);

    /**
     * Handle pill click for selection
     * @param email - Email of the pill
     * @param e - React mouse event
     */
    const handlePillClick = useCallback(
        (email: string, e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const newSelectedPills = new Set(selectedPills);
            if (newSelectedPills.has(email)) {
                newSelectedPills.delete(email);
            } else {
                newSelectedPills.add(email);
            }
            setSelectedPills(newSelectedPills);
        },
        [selectedPills],
    );

    /**
     * Handle closing dropdown
     */
    const handleCloseDropdown = useCallback(() => {
        setShowDropdown(false);
    }, []);

    const closeComposeModal = () => {
        dispatch(
            setComposeEmailModal({
                show: false,
                source: '',
                selectedRequirements: [],
                orgName: '',
            }),
        );
    };

    /**
     * Handles the preview action by fetching template and saving the data in session storage and opening preview tab
     * @param response - Response from sendEmailNotification
     * @returns Promise<void>
     */
    const handlePreviewAction = useCallback(
        async (response: any, targetWindow?: Window | null): Promise<void> => {
            const templateValues = response?.data?.notificationRequest?.templateValuesMap || {};
            const templateId = response?.data?.notificationRequest?.templateId || '';

            const templateResponse = await getEmailTemplate({ templateId });

            if (!templateResponse || typeof templateResponse !== 'object' || !('data' in templateResponse)) {
                throw new Error('Invalid template response');
            }

            const responseData = templateResponse.data as any;
            const template = responseData?.responseBody?.template?.emailTemplate || {};

            // Get the subject template and replace placeholders with actual values
            const subjectTemplate = responseData?.responseBody?.template?.emailTemplate?.subject || '';
            const processedSubject = replaceTemplatePlaceholders(subjectTemplate, templateValues);

            const toEmails = selectedUsers.map((user) => user?.emailId || '').filter(Boolean);
            const toField = toEmails.length > 1 ? toEmails.join(', ') : toEmails[0] || '';

            const previewUrl = `${window.location.origin}${BASE_ROUTES.PREVIEW_EMAIL}`;

            // Document-backed system attachments (EL / Bureau-KYC / One Pager)
            // ride the email as docIds but were missing from the preview's
            // attachment strip — only manual uploads were shown. They share the
            // { docName, id } shape, so merge them ahead of the manual files
            // (matching the modal's pill order). Data Pending has no doc (it's an
            // in-body table) so it is correctly absent here.
            const systemAttachmentFiles = Object.values(systemAttachmentDocs).filter(Boolean) as ManualUploadFile[];
            const previewAttachments = [...systemAttachmentFiles, ...filesMetaData];

            const previewData = {
                templateHTML: template?.html || '',
                toField,
                subject: processedSubject,
                templateValues,
                filesMetaData: previewAttachments,
            };
            sessionStorage.setItem('emailPreviewData', JSON.stringify(previewData));

            // When a chip already opened a tab (popup-safe), navigate it; otherwise
            // open a fresh tab as before.
            if (targetWindow) targetWindow.location.href = previewUrl;
            else window.open(previewUrl, ATTACHMENT_PILL_NEW_TAB_TARGET);
        },
        [selectedUsers, filesMetaData, systemAttachmentDocs, replaceTemplatePlaceholders],
    );

    /**
     * Common function to handle email notification with shared logic
     * @param notificationMode - "READ" for preview, "WRITE" for send
     * @returns Promise<void>
     */
    const handleEmailNotification = useCallback(
        async (
            notificationMode: 'READ' | 'WRITE',
            options?: { onReadResponse?: (response: any) => void | Promise<void> },
        ): Promise<void> => {
            // Determine flowId and metaData based on source
            const flowId = isLenderQuery ? 'AICA_LENDER_DEAL_EMAIL' : 'AICA_COMPANY_EMAIL';
            const metaData = isLenderQuery
                ? { investorDealId: searchParams.get('dealId')?.toString() || composeEmailData.dealId || '' }
                : { orgId };
            // Upload base64 images to S3 only on send, not preview
            // Preview renders HTML in browser — needs base64/presigned URLs, not docIds
            let processedText: Value = userInputTextRef.current || EMPTY_EDITOR_VALUE;
            if (notificationMode === 'WRITE') {
                const source = isLenderQuery ? COMPOSE_EMAIL_UPLOAD_SOURCE.LENDER : COMPOSE_EMAIL_UPLOAD_SOURCE.COMPANY;
                processedText = (await processImagesBeforeSave(processedText as any, [], source)) as Value;
            }
            // plateToHtml already returns '' for an empty doc, so no '<div></div>' special-case.
            // nestLists: render true nested <ul>/<ol> with depth-cycled markers
            // (1 → a → i) so the email body matches what the author typed — modern
            // mail clients handle nested lists + list-style-type fine.
            const userProvidedBodyHtml = await plateToHtml(processedText, { nestLists: true, emailStyles: true });

            // Document-backed system attachments (EL / Bureau-KYC / One Pager) are
            // generated client-side, uploaded, and sent as ordinary docIds — the
            // backend has no attach-flag support for them (only attachDataPendingList).
            const systemDocIds = Object.values(systemAttachmentDocs)
                .map((doc) => Number(doc?.id) || 0)
                .filter((id) => id !== 0);
            const manualDocIds = filesMetaData.map((file) => Number(file?.id) || 0).filter((id) => id !== 0);
            const payload = {
                userIds: selectedUsers.map((user) => user?.userId || ''),
                notificationMode,
                flowId,
                // The Data Pending list is rendered into the editor body (and
                // serialised into userProvidedBody), so suppress the backend's
                // appended table to avoid a duplicate. Only fall back to the
                // backend flag when the toggle is on but we had no requirements
                // (neither carried in nor fetched) to build the in-body table.
                attachDataPendingList: systemToggles[SystemAttachmentKey.DATA_PENDING] && effectiveDataPendingRequirements.length === 0,
                metaData,
                notificationSenderUserId: platformUserId || '',
                selectedRequirements: composeEmailData.selectedRequirements || [],
                docIds: [...systemDocIds, ...manualDocIds],
                userProvidedBody: userProvidedBodyHtml,

                ...(attachSignature && { footerSignature: getSignatureData() }),
            };

            return new Promise((resolve, reject) => {
                sendEmailNotification(payload, {
                    onSuccess: async (response) => {
                        try {
                            //If previewing, get the template and open the preview in a new tab
                            if (notificationMode === 'READ') {
                                // A document chip click reuses this READ call (the backend
                                // generates the system PDFs here) but extracts the doc URL
                                // instead of opening the email preview.
                                if (options?.onReadResponse) {
                                    await options.onReadResponse(response);
                                } else {
                                    await handlePreviewAction(response);
                                }
                                resolve();
                            } else {
                                toast.success('Email sent successfully!');
                                Mixpanel.track(AICA_COMPOSE_EMAIL_SENT, {
                                    source: composeEmailData?.source,
                                    requirementCount: composeEmailData?.selectedRequirements?.length,
                                    attachmentCount: payload.docIds.length,
                                    recipientCount: selectedUsers.length,
                                });
                                // Trigger requirements refresh after successful email send
                                dispatch(triggerRequirementsRefresh());
                                closeComposeModal();
                                resolve();
                            }
                        } catch (error) {
                            reject(error);
                        }
                    },
                    onError: (error) => {
                        const defaultErrorMessage =
                            notificationMode === 'READ'
                                ? 'Error previewing email. Please try again.'
                                : 'Error sending email. Please try again.';
                        const errorMessage = error?.responseData?.responseMessage || defaultErrorMessage;
                        toast.error(errorMessage);
                        const action = notificationMode === 'READ' ? 'previewing' : 'sending';
                        reject(new Error(`Error ${action} email: ${error}`));
                    },
                });
            });
        },
        [
            selectedUsers,
            systemToggles,
            systemAttachmentDocs,
            effectiveDataPendingRequirements,
            orgId,
            user?.userData?.userId,
            composeEmailData,
            filesMetaData,
            closeComposeModal,
            searchParams,
            attachSignature,
            getSignatureData,
            isLenderQuery,
            dispatch,
            handlePreviewAction,
        ],
    );

    /**
     * Handles preview action with loading state
     * @returns Promise<void>
     */
    const handlePreview = useCallback(async (): Promise<void> => {
        try {
            setIsPreviewLoading(true);
            await handleEmailNotification('READ');
        } catch (error) {
            console.error('Error in preview action:', error);
        } finally {
            setIsPreviewLoading(false);
        }
    }, [handleEmailNotification]);

    /**
     * Handles send action with loading state and modal closing
     * @returns Promise<void>
     */
    const handleSend = async (): Promise<void> => {
        try {
            setIsSendLoading(true);
            await handleEmailNotification('WRITE');
        } catch (error) {
            console.error('Error in send action:', error);
        } finally {
            setIsSendLoading(false);
        }
    };

    // The system attachment rows visible in the dropdown: Data Pending is always
    // shown; Engagement Letter / Bureau & KYC Consent only when configured.
    const visibleSystemAttachmentKeys = useMemo<SystemAttachmentKey[]>(
        () => SYSTEM_ATTACHMENT_ORDER.filter((key) => systemConfigured[key]),
        [systemConfigured],
    );

    // Attached files shown as hyperlinked pills below the editor. Order: system
    // pills first (DATA_PENDING → EL → Bureau-KYC → One Pager), then manual uploads.
    // Data Pending is flag-driven (no docId — it's an in-email table); the other
    // system pills appear once their doc has been generated + uploaded.
    const attachmentPills = useMemo<AttachmentPill[]>(() => {
        const systemPills: AttachmentPill[] = SYSTEM_ATTACHMENT_ORDER.flatMap((key): AttachmentPill[] => {
            if (key === SystemAttachmentKey.DATA_PENDING) {
                return systemConfigured[key] && systemToggles[key]
                    ? [{ id: key, label: SYSTEM_ATTACHMENT_LABELS[key], systemKey: key, isManual: false }]
                    : [];
            }
            const doc = systemAttachmentDocs[key];
            return doc ? [{ id: key, label: SYSTEM_ATTACHMENT_LABELS[key], systemKey: key, docId: doc.id, isManual: false }] : [];
        });

        const manualPills: AttachmentPill[] = filesMetaData.map((file) => ({
            id: `manual-${file?.id}`,
            label: file?.docName || '',
            docId: file?.id,
            isManual: true,
        }));

        return [...systemPills, ...manualPills];
    }, [systemConfigured, systemToggles, systemAttachmentDocs, filesMetaData]);

    // Open a manually-uploaded file in a new tab via its presigned URL.
    // The blank tab is opened SYNCHRONOUSLY inside the click gesture — calling
    // window.open after the `await` loses user-activation and browsers block it
    // as a popup, so the document would silently never open. We grab the tab
    // now and redirect it once the presigned URL resolves.
    const openManualFile = useCallback(async (docId: number) => {
        const newTab = window.open('', ATTACHMENT_PILL_NEW_TAB_TARGET);
        try {
            const signed = await getPresignedUrl({ docId }, false);
            if (signed?.uri) {
                if (newTab) newTab.location.href = signed.uri;
                else window.open(signed.uri, ATTACHMENT_PILL_NEW_TAB_TARGET);
            } else {
                newTab?.close();
                toast.error('Unable to open this file. Please try again.');
            }
        } catch (error) {
            newTab?.close();
            console.error('Error opening attached file:', error);
            toast.error('Unable to open this file. Please try again.');
        }
    }, []);

    // Pill click router:
    //   • Manual upload               → its presigned document URL in a new tab.
    //   • EL / Bureau-KYC / One Pager → reopen the doc's editor drawer (view/edit;
    //                                    re-saving regenerates + re-uploads the PDF).
    //   • Data Pending                → open the email preview (the table lives in
    //                                    the email body, there is no standalone doc).
    const handleAttachmentPillClick = useCallback(
        (pill: AttachmentPill) => {
            if (pill.isManual && pill.docId) {
                openManualFile(pill.docId);
                return;
            }
            if (pill.systemKey === SystemAttachmentKey.DATA_PENDING) {
                handlePreview();
                return;
            }
            if (pill.systemKey && DOC_TYPE_SYSTEM_KEYS.includes(pill.systemKey)) {
                openSystemDocDrawer(pill.systemKey);
                return;
            }
        },
        [openManualFile, handlePreview, openSystemDocDrawer],
    );

    return {
        isModalLoading,
        isPreviewLoading,
        isSendLoading,
        isUploadingFiles,
        totalAttachmentBytes,
        isOverAttachmentLimit,
        handlePreview,
        handleSend,
        handleFileUpload,
        handleToggleSystemAttachment,
        handleToggleAttachSignature,
        handleRemoveManualFile,
        handleUploadStatusChange,
        handleAddUser,
        handleRemoveUser,
        selectedUsers,
        selectableUsers,
        ownerOptions,
        isLenderQuery,
        filesMetaData,
        sendNeedInfo,
        systemToggles,
        visibleSystemAttachmentKeys,
        attachmentPills,
        handleAttachmentPillClick,
        attachSignature,
        userInputText,
        // Read-only ref to the latest editor value (bypasses the trailing debounce
        // above). Magic Sign's override send/preview handlers in ComposeEmailModal
        // build the outgoing HTML from this ref instead of userInputText so a
        // click right after typing never serialises a stale, pre-debounce body.
        userInputTextRef,
        categoryId,
        flowId,
        closeComposeModal,
        composeEmailData,
        setUserInputText: (value: Value) => {
            userInputTextRef.current = value;
            if (userInputTextDebounceRef.current) window.clearTimeout(userInputTextDebounceRef.current);
            userInputTextDebounceRef.current = window.setTimeout(() => setUserInputText(value), 300);
        },
        handleEditorMount,
        setFilesMetaData,
        orgId,
        getSignatureData,
        // User Email Input related
        inputValue,
        showDropdown,
        filteredUsers,
        selectedPills,
        isValidatingEmail,
        inputRef,
        isSelectingFromDropdown,
        handleInputChange,
        handleKeyPress,
        handleDropdownItemClick,
        handleInputBlur,
        handleInputFocus,
        handlePillClick,
        handleCloseDropdown,
        setSelectedPills,
    };
};
